import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Box, Typography, Button, Chip, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, CircularProgress, Collapse, IconButton
} from '@mui/material';
import {
  SwapHoriz, Add, ExpandMore, ExpandLess, CheckCircle, Cancel, Edit
} from '@mui/icons-material';
import changeOrderService from '../../services/changeOrderService';
import DigitalSignatureCapture from '../signatures/DigitalSignatureCapture';
import { brand, status as statusColors } from '../../theme/colors';

const STATUS_CHIP = {
  draft: { label: 'Draft', color: 'default' },
  pending_approval: { label: 'Awaiting Decision', color: 'warning' },
  changes_requested: { label: 'Changes Requested', color: 'warning' },
  awaiting_funding: { label: 'Approved — Fund to Start', color: 'info' },
  applied: { label: 'In Effect', color: 'success' },
  declined: { label: 'Declined', color: 'error' },
  withdrawn: { label: 'Withdrawn', color: 'default' },
};

const fmtDelta = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return null;
  return `${v > 0 ? '+' : '−'}$${Math.abs(v).toLocaleString()}`;
};

/**
 * ChangeOrdersPanel — propose, review, sign, and track scope/cost/timeline
 * variations on a project. Rendered for both the owner and the contractor;
 * available actions depend on who proposed each change order.
 */
const ChangeOrdersPanel = ({ projectId, project, milestones = [], user, onChanged }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState('');

  // propose dialog state
  const [proposeOpen, setProposeOpen] = useState(false);
  const [form, setForm] = useState({
    kind: 'amend_milestone', milestoneId: '', title: '',
    scopeDescription: '', costDelta: '', timelineDeltaDays: '', newMilestoneName: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // decision state
  const [signatureFor, setSignatureFor] = useState(null); // CO being approved
  const [commentDialog, setCommentDialog] = useState(null); // { co, action: 'request-changes'|'decline' }
  const [comment, setComment] = useState('');

  const isParty = user && (String(project?.owner_id) === String(user.id) || String(project?.contractor_id) === String(user.id));
  const myRole = String(project?.owner_id) === String(user?.id) ? 'client' : 'contractor';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await changeOrderService.listForProject(projectId);
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      // Non-parties get 403 — render nothing rather than an error
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId && isParty) load(); }, [projectId, isParty, load]);

  const eligibleMilestones = milestones.filter(
    (m) => !['completed', 'verified', 'paid'].includes(m.status)
  );

  const resetForm = () => setForm({
    kind: 'amend_milestone', milestoneId: '', title: '',
    scopeDescription: '', costDelta: '', timelineDeltaDays: '', newMilestoneName: '',
  });

  const handlePropose = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        kind: form.kind,
        milestoneId: form.kind === 'amend_milestone' ? form.milestoneId : undefined,
        title: form.title,
        scopeDescription: form.scopeDescription,
        costDelta: form.costDelta === '' ? 0 : Number(form.costDelta),
        timelineDeltaDays: form.timelineDeltaDays === '' ? 0 : parseInt(form.timelineDeltaDays, 10),
        newMilestoneSpec: form.kind === 'new_work' ? { name: form.newMilestoneName || form.title } : undefined,
      };
      const draft = await changeOrderService.createDraft(projectId, payload);
      await changeOrderService.submit(draft.id);
      setProposeOpen(false);
      resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to propose change order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignatureComplete = async (signaturePayload) => {
    const co = signatureFor;
    setSignatureFor(null);
    setError('');
    try {
      const updated = await changeOrderService.approve(co.id, signaturePayload);
      await load();
      if (onChanged) onChanged();
      // Increases need funding — hand the user to the wallet flow
      if (updated.status === 'awaiting_funding') {
        setError(''); // not an error; surfaced via the status chip + alert below
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve change order');
    }
  };

  const handleComment = async () => {
    const { co, action } = commentDialog;
    setSubmitting(true);
    setError('');
    try {
      if (action === 'request-changes') await changeOrderService.requestChanges(co.id, comment);
      else await changeOrderService.decline(co.id, comment);
      setCommentDialog(null);
      setComment('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async (co) => {
    setError('');
    try {
      await changeOrderService.withdraw(co.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to withdraw');
    }
  };

  if (!isParty) return null;

  const needsMyDecision = (co) => co.status === 'pending_approval' && co.proposer_role !== myRole;
  const milestoneName = (id) => milestones.find((m) => m.id === id)?.name
    || milestones.find((m) => m.id === id)?.title || id;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHoriz sx={{ color: brand.primary }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Change Orders</Typography>
          </Box>
          <Button startIcon={<Add />} variant="outlined" size="small" onClick={() => setProposeOpen(true)}>
            Propose Change
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scope, cost, or timeline changes both parties sign before they take effect.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : orders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No change orders yet. Propose one when the scope, cost, or timeline needs to change.
          </Typography>
        ) : (
          orders.map((co) => {
            const chip = STATUS_CHIP[co.status] || { label: co.status, color: 'default' };
            const open = expandedId === co.id;
            return (
              <Box key={co.id} sx={{ border: '1px solid', borderColor: needsMyDecision(co) ? statusColors.warning : 'divider', borderRadius: 1.5, p: 1.5, mb: 1.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                     onClick={() => setExpandedId(open ? null : co.id)}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{co.title}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Chip size="small" label={chip.label} color={chip.color} />
                      {fmtDelta(co.cost_delta) && <Chip size="small" variant="outlined" label={fmtDelta(co.cost_delta)} />}
                      {Number(co.timeline_delta_days) !== 0 && (
                        <Chip size="small" variant="outlined" label={`${co.timeline_delta_days > 0 ? '+' : ''}${co.timeline_delta_days} days`} />
                      )}
                    </Stack>
                  </Box>
                  <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </Box>

                <Collapse in={open}>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{co.scope_description}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {co.kind === 'amend_milestone'
                      ? `Changes milestone: ${milestoneName(co.milestone_id)}`
                      : 'Adds new work as its own milestone'}
                    {' · '}Proposed by the {co.proposer_role}
                  </Typography>
                  {co.decision_comment && (
                    <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                      <Typography variant="caption"><strong>Decision note:</strong> {co.decision_comment}</Typography>
                    </Alert>
                  )}
                  {co.status === 'awaiting_funding' && myRole === 'client' && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Approved and signed — fund the {fmtDelta(co.cost_delta)} difference from the Project Wallet
                      ("Add Money to Wallet") so the contractor can start.
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {needsMyDecision(co) && (
                      <>
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />}
                                onClick={(e) => { e.stopPropagation(); setSignatureFor(co); }}>
                          Approve &amp; Sign
                        </Button>
                        <Button size="small" startIcon={<Edit />}
                                onClick={(e) => { e.stopPropagation(); setCommentDialog({ co, action: 'request-changes' }); }}>
                          Request Changes
                        </Button>
                        <Button size="small" color="error" startIcon={<Cancel />}
                                onClick={(e) => { e.stopPropagation(); setCommentDialog({ co, action: 'decline' }); }}>
                          Decline
                        </Button>
                      </>
                    )}
                    {['draft', 'pending_approval'].includes(co.status) && co.proposer_role === myRole && (
                      <Button size="small" color="warning"
                              onClick={(e) => { e.stopPropagation(); handleWithdraw(co); }}>
                        Withdraw
                      </Button>
                    )}
                  </Stack>
                </Collapse>
              </Box>
            );
          })
        )}

        {/* Propose dialog */}
        <Dialog open={proposeOpen} onClose={() => !submitting && setProposeOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Propose a Change Order</DialogTitle>
          <DialogContent>
            <TextField select fullWidth label="What kind of change?" value={form.kind} sx={{ mt: 1, mb: 2 }}
                       onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}>
              <MenuItem value="amend_milestone">Change an existing milestone</MenuItem>
              <MenuItem value="new_work">Add new work (new milestone)</MenuItem>
            </TextField>
            {form.kind === 'amend_milestone' ? (
              <TextField select fullWidth label="Milestone" value={form.milestoneId} sx={{ mb: 2 }}
                         onChange={(e) => setForm((f) => ({ ...f, milestoneId: e.target.value }))}>
                {eligibleMilestones.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.title || m.name}</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField fullWidth label="New milestone name" value={form.newMilestoneName} sx={{ mb: 2 }}
                         onChange={(e) => setForm((f) => ({ ...f, newMilestoneName: e.target.value }))} />
            )}
            <TextField fullWidth label="Title" value={form.title} sx={{ mb: 2 }}
                       onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <TextField fullWidth multiline rows={3} label="What's changing and why?" value={form.scopeDescription} sx={{ mb: 2 }}
                       onChange={(e) => setForm((f) => ({ ...f, scopeDescription: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <TextField fullWidth type="number" label="Cost change (USD, − for savings)" value={form.costDelta}
                         onChange={(e) => setForm((f) => ({ ...f, costDelta: e.target.value }))} />
              <TextField fullWidth type="number" label="Timeline change (days)" value={form.timelineDeltaDays}
                         onChange={(e) => setForm((f) => ({ ...f, timelineDeltaDays: e.target.value }))} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Submitting sends this to the {myRole === 'client' ? 'contractor' : 'project owner'} to approve, request changes, or decline.
              Cost increases must be funded before work starts.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProposeOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handlePropose}
                    disabled={submitting || !form.title.trim() || !form.scopeDescription.trim()
                      || (form.kind === 'amend_milestone' && !form.milestoneId)}>
              {submitting ? 'Submitting…' : 'Submit for Approval'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Comment dialog (request changes / decline) */}
        <Dialog open={!!commentDialog} onClose={() => !submitting && setCommentDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{commentDialog?.action === 'decline' ? 'Decline Change Order' : 'Request Changes'}</DialogTitle>
          <DialogContent>
            <TextField fullWidth multiline rows={3} sx={{ mt: 1 }} autoFocus
                       label={commentDialog?.action === 'decline' ? 'Why are you declining?' : 'What should change?'}
                       value={comment} onChange={(e) => setComment(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCommentDialog(null)} disabled={submitting}>Cancel</Button>
            <Button variant="contained" color={commentDialog?.action === 'decline' ? 'error' : 'primary'}
                    onClick={handleComment} disabled={submitting || !comment.trim()}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Signature capture for approval */}
        {signatureFor && (
          <DigitalSignatureCapture
            open={!!signatureFor}
            onClose={() => setSignatureFor(null)}
            onSignatureComplete={handleSignatureComplete}
            documentType="change_order"
            documentId={signatureFor.id}
            documentData={{
              title: signatureFor.title,
              scope: signatureFor.scope_description,
              costDelta: Number(signatureFor.cost_delta),
              timelineDeltaDays: signatureFor.timeline_delta_days,
            }}
            signerName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email}
            signerEmail={user?.email}
            consentText={`I approve change order "${signatureFor.title}" (${fmtDelta(signatureFor.cost_delta) || 'no cost change'}) as a binding amendment to the project agreement.`}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ChangeOrdersPanel;
